// External Imports
import PropTypes from 'prop-types';
import { Component } from 'react';
import { connect } from 'react-redux';

// Internal Imports
import { actionQueryAbTester } from '../../actions/index.mjs';
import { selectIsBabbelTrackingUuidSet } from '../../selectors/selectIsBabbelTrackingUuidSet.mjs';
import { lazyApiClient } from '../../services/babbelApi/apiClient.mjs';
import { logger } from '../../utils/rollbar.mjs';

// Component Definition
class AbTester extends Component {
  componentDidMount() {
    this.initializeAbTesterAndQuery();
  }

  componentDidUpdate() {
    const { isTrackingUuidSet } = this.props;
    if (isTrackingUuidSet && !this.abTesterQueried && this.apiClient && this.AbTesterApi) {
      this.queryAbTester();
    }
  }

  initializeAbTesterAndQuery = async () => {
    const { isTrackingUuidSet } = this.props;
    try {
      this.apiClient = await lazyApiClient();
      const abTesterModule = await import('@lessonnine/ab-tester.api.js');
      this.AbTesterApi = abTesterModule.default;
    } catch (error) {
      logger.error('Error importing api-client.js or ab-tester.api.js.', error);
      return;
    }
    if (isTrackingUuidSet) {
      this.abTesterQueried = true;
      this.queryAbTester();
    }
  };

  queryAbTester = async () => {
    const { dispatchQueryAbTesterAction, shouldTrack } = this.props;
    const experimentResult = new this.AbTesterApi.ExperimentResult(this.apiClient);
    const { experimentGroup, experimentTarget, magazineEditionLanguage } = this.props;
    try {
      const bucket = await experimentResult.get({
        locale: magazineEditionLanguage,
        target: experimentTarget,
        track: shouldTrack,
      });
      const { destination_value: destinationValue } = bucket;
      const isInExperimentGroup = destinationValue === experimentGroup;
      dispatchQueryAbTesterAction({
        bucket,
        isInExperimentGroup,
        isLoading: false,
        wasQueried: true,
      });
    } catch (error) {
      dispatchQueryAbTesterAction({
        error,
        isInExperimentGroup: false,
        isLoading: false,
        wasQueried: true,
      });
      if (!error.statusCode) {
        logger.error(
          `Unexpected error without statusCode in queryAbTester(): ${error.message}`,
          error,
        );
        return;
      }
      // 422 code is an “expected state” from Ab-tester response when the user is not in population
      if (error.statusCode !== 422) {
        logger.error(
          `Error in queryAbTester() when getting experimentResult: unexpected status code ${error.statusCode} received`,
          error,
        );
      }
    }
  };

  // eslint-disable-next-line class-methods-use-this -- renderless component
  render() {
    return null; // eslint-disable-line unicorn/no-null -- React expects null for empty renders
  }
}

const mapStateToProps = (state) => ({
  isTrackingUuidSet: selectIsBabbelTrackingUuidSet(state),
});

const mapDispatchToProps = (dispatch) => ({
  dispatchQueryAbTesterAction: (payload) => {
    dispatch(actionQueryAbTester(payload));
  },
});

const withRedux = connect(mapStateToProps, mapDispatchToProps);
const AbTesterHoc = withRedux(AbTester);

AbTester.propTypes = {
  dispatchQueryAbTesterAction: PropTypes.func.isRequired,
  experimentGroup: PropTypes.string,
  experimentTarget: PropTypes.string.isRequired,
  isTrackingUuidSet: PropTypes.bool.isRequired,
  magazineEditionLanguage: PropTypes.string.isRequired,
  shouldTrack: PropTypes.bool,
};

AbTester.defaultProps = {
  experimentGroup: 'test',
  shouldTrack: true,
};

// Module Exports
export { AbTesterHoc as AbTester };
